import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { acknowledgementsAPI, documentsAPI } from '../api/client';
import { extractErrorMessage } from '../utils/errors';

/**
 * My Tasks (GitHub issue #60) -- a document owner's workload in one place:
 * what's coming due, what's currently mid-workflow, and everything else they
 * own. No RequirePermission wrapper -- owning a document requires no
 * permission (owner is picked from GET /api/users/selectable, any
 * authenticated user), same reasoning /approvals and /profile are ungated.
 *
 * Buckets and their precedence come from the backend
 * (routers/documents.py::get_my_tasks / MyTasksResponse) -- this page never
 * re-derives "coming due" or "in progress" from raw fields, so there is
 * exactly one definition of each bucket.
 */
const BUCKETS = [
  { key: 'in_progress', label: 'In Progress', color: 'var(--info-ink)',
    empty: "Nothing you own is currently in draft or under approval." },
  { key: 'coming_due', label: 'Coming Due', color: 'var(--warning-ink)',
    empty: 'Nothing you own is coming due.' },
  { key: 'other', label: 'All Others', color: 'var(--muted)',
    empty: "Nothing else -- everything you own is either in progress or coming due." },
];

function MyTasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState(null);
  // Kept separate from `tasks` on purpose -- see the acknowledgements section
  // in the render below for why this is not a fourth bucket.
  const [acknowledgements, setAcknowledgements] = useState([]);
  // @mentions awaiting the caller (issue #75). Separate state and a separate
  // endpoint for the same reason acknowledgements are -- see the Comments
  // section in the render below.
  const [mentions, setMentions] = useState([]);
  // Issue #190 -- "show dismissed" toggle on the same Comments card, reading
  // the same endpoint with a flag rather than a second one.
  const [showDismissed, setShowDismissed] = useState(false);
  // Documents the caller was named a contributor on (issue #187). Separate
  // state and a separate endpoint for the same reason mentions/
  // acknowledgements are -- see the Contributions section in the render below.
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const activeBucket = BUCKETS.some(b => b.key === searchParams.get('bucket'))
    ? searchParams.get('bucket')
    : 'in_progress';

  useEffect(() => {
    documentsAPI.myTasks()
      .then((res) => setTasks(res.data))
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));

    // Non-fatal: an attestation list failing must not blank out the owner
    // workload this page is primarily about.
    acknowledgementsAPI.mine()
      .then((res) => setAcknowledgements(res.data.outstanding || []))
      .catch((err) => console.error('Error loading acknowledgements:', err));

    // Non-fatal for the same reason.
    documentsAPI.myContributions()
      .then((res) => setContributions(res.data))
      .catch((err) => console.error('Error loading contributions:', err));
  }, []);

  // Mentions are re-fetched whenever the "show dismissed" toggle changes,
  // rather than filtered client-side -- include_dismissed is the backend's
  // own definition of the combined list. Runs once on mount too, since
  // showDismissed starts false.
  useEffect(() => {
    documentsAPI.myMentions({ include_dismissed: showDismissed })
      .then((res) => setMentions(res.data))
      .catch((err) => console.error('Error loading mentions:', err));
  }, [showDismissed]);

  const reloadMentions = () => {
    documentsAPI.myMentions({ include_dismissed: showDismissed })
      .then((res) => setMentions(res.data))
      .catch((err) => console.error('Error reloading mentions:', err));
  };

  const dismissMention = (id) => {
    documentsAPI.dismissMention(id)
      .then(reloadMentions)
      .catch((err) => console.error('Error dismissing mention:', err));
  };

  const undismissMention = (id) => {
    documentsAPI.undismissMention(id)
      .then(reloadMentions)
      .catch((err) => console.error('Error restoring mention:', err));
  };

  const selectBucket = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set('bucket', key);
    setSearchParams(next);
  };

  if (loading) {
    return <div className="loading">Loading your tasks...</div>;
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  const bucket = BUCKETS.find(b => b.key === activeBucket);
  const rows = tasks ? tasks[activeBucket] : [];

  return (
    <div>
      <div className="page-header">
        <h2>📋 My Tasks</h2>
      </div>

      <p style={{ fontSize: '13px', color: 'var(--muted)', marginTop: 0 }}>
        Documents you own{tasks ? `, coming due within ${tasks.horizon_days} days` : ''}.
      </p>

      {/* Comments (issue #75) -- where an @mention lands. ABOVE the tiles and
          OUTSIDE them, the same call the acknowledgements card below makes and
          for the same reason: the three buckets are a disjoint partition of
          documents you OWN, and somebody asking you a question on a passage of
          THEIR policy is not that. It also has to work with email switched off
          entirely, which is why this is the primary delivery of a mention and
          not a convenience on top of one. */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>💬 Comments for you ({mentions.length})</h3>
          {/* Issue #190: the same endpoint with a flag, not a second list. */}
          <label style={{ fontSize: '13px', fontWeight: 400, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input type="checkbox" checked={showDismissed}
                   onChange={(e) => setShowDismissed(e.target.checked)} />
            Show dismissed
          </label>
        </div>
        {mentions.length === 0 ? (
          <div className="empty-state">
            <p>{showDismissed
              ? 'No comments, dismissed or otherwise.'
              : 'No comments are waiting on you right now.'}</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>From</th>
                  <th>Comment</th>
                  <th>Policy</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {mentions.map((row) => (
                  <tr key={row.id} style={row.dismissed_at ? { color: 'var(--muted)' } : undefined}>
                    <td>
                      {row.author_name || 'Someone'}
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                        {new Date(row.created_at).toLocaleString()}
                      </div>
                    </td>
                    {/* A plain text node. Comment bodies are never rendered as
                        markup anywhere in this app. */}
                    <td style={{ maxWidth: '380px' }}>{row.body}</td>
                    <td>
                      <Link to={row.document_revision_id
                        ? `/documents/${row.document_id}/revisions/${row.document_revision_id}`
                        : `/documents/${row.document_id}`}>
                        <strong>{row.document_title}</strong>
                      </Link>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                        {row.hierarchy_display_path || '-'}
                        {row.document_version_label ? ` — ${row.document_version_label}` : ''}
                      </div>
                    </td>
                    <td>
                      {row.dismissed_at ? (
                        <>
                          <span style={{ fontSize: '12px' }}>
                            Dismissed {new Date(row.dismissed_at).toLocaleDateString()}
                          </span>
                          {' — '}
                          <button type="button" className="btn-link"
                                  onClick={() => undismissMention(row.id)}>
                            Undo
                          </button>
                        </>
                      ) : (
                        <button type="button" className="btn-link"
                                onClick={() => dismissMention(row.id)}>
                          Dismiss
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Documents the caller was named a contributor on (issue #187). ABOVE
          the tiles and OUTSIDE them, for the same reason Comments/
          Acknowledgements are: the three buckets are a disjoint partition of
          documents you OWN, and being invited onto someone else's policy is
          not that. No action column -- a contributor grant is standing
          access, not a task to clear. */}
      {contributions.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header">
            <h3>🤝 Documents you contribute to ({contributions.length})</h3>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Policy</th>
                  <th>Your access</th>
                </tr>
              </thead>
              <tbody>
                {contributions.map((row) => (
                  <tr key={row.document_id}>
                    <td>
                      <Link to={`/documents/${row.document_id}`}>
                        <strong>{row.document_title}</strong>
                      </Link>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                        {row.hierarchy_display_path || '-'}
                        {row.document_version_label ? ` — ${row.document_version_label}` : ''}
                      </div>
                    </td>
                    <td>
                      {row.can_read && <span className="badge" style={{ marginRight: '4px' }}>Read</span>}
                      {row.can_comment && <span className="badge" style={{ marginRight: '4px' }}>Comment</span>}
                      {row.can_edit && <span className="badge">Edit</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Acknowledgements sit ABOVE the tiles and deliberately OUTSIDE them,
          rather than becoming a fourth bucket. The three buckets are documented
          as a disjoint partition of documents you OWN; an attestation is a
          reader's duty on somebody else's policy, has a different audience
          (everyone) and comes from a different endpoint. */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h3>✍️ Awaiting your acknowledgement ({acknowledgements.length})</h3>
        </div>
        {acknowledgements.length === 0 ? (
          <div className="empty-state">
            <p>Nothing is awaiting your acknowledgement right now.</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Location</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                {acknowledgements.map((row) => (
                  <tr key={row.document_id}>
                    <td>
                      <Link to={`/documents/${row.document_id}`}>
                        <strong>{row.document_title}</strong>
                      </Link>
                    </td>
                    <td>{row.hierarchy_display_path || '-'}</td>
                    <td>{row.document_version_label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="stats-grid stats-grid--compact" style={{ marginBottom: '20px' }}>
        {BUCKETS.map((b) => (
          <button
            key={b.key}
            type="button"
            className="stat-card stat-card--link stat-card--compact"
            style={{
              border: b.key === activeBucket ? '2px solid var(--teal-700)' : undefined,
              cursor: 'pointer', textAlign: 'left', font: 'inherit', width: '100%',
            }}
            onClick={() => selectBucket(b.key)}
          >
            <div className="stat-label">{b.label}</div>
            <div className="stat-value" style={{ color: b.color }}>
              {tasks ? tasks[b.key].length : 0}
            </div>
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <h3>{bucket.label} ({rows.length})</h3>
        </div>
        {rows.length === 0 ? (
          <div className="empty-state">
            <p>{bucket.empty}</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Location</th>
                  <th>Policy Area</th>
                  <th>Status</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((doc) => (
                  <tr key={doc.id}>
                    <td>
                      <Link to={`/documents/${doc.id}`}><strong>{doc.title}</strong></Link>
                      <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                        {doc.version_label}
                      </div>
                    </td>
                    <td>{doc.hierarchy_display_path || '-'}</td>
                    <td>
                      {doc.policy_areas?.length > 0
                        ? doc.policy_areas.map(pa => pa.name).join(', ')
                        : <span style={{ color: 'var(--muted-light)' }}>—</span>}
                    </td>
                    <td><span className={`badge badge-${doc.status}`}>{doc.status}</span></td>
                    <td>{doc.expires_at ? new Date(doc.expires_at).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default MyTasksPage;
